/**
 * LOGOS v5.0 — Emergence Swarm Animation
 * 
 * Overdamped Langevin Dynamics for 28 particles in ℝ²
 * Each particle represents one of the 28 consciousness dimensions.
 * 
 * Physics Model:
 *   dr_i/dt = F_central + F_radial + F_domain + F_repulsion + F_mandala + F_noise
 * 
 * where:
 *   F_central  = -α·Λ·(r_i - r₀)           — harmonic well, depth ∝ Λ²
 *   F_radial   = -β·(|r_i-r₀| - R_eq(s_i))  — value-dependent equilibrium radius
 *   F_domain   = -κ·Σ_{j∈D_k}(r_i - r_j)    — intra-domain cohesion
 *   F_repulsion = μ·Σ_{j≠i} r̂_{ij}/|r_{ij}|² — short-range repulsion (d < d_min)
 *   F_mandala  = -ψ·σ(Λ-0.6)·(r_i - r_i*)   — geometric attractor near A⁺
 *   F_noise    = σ_n·√S·ξ(t)                  — thermal noise (Entropy = temperature)
 * 
 * Integration: Euler-Maruyama scheme for SDE
 *   r_i(t+Δt) = r_i(t) + Δt·F_total + √(Δt)·σ_n·√S·N(0,1)
 * 
 * Phase behavior (Ginzburg-Landau analogy):
 *   Λ > 0.65  → Ordered phase (crystallization into mandala)
 *   0.35 < Λ < 0.65 → Fluid phase (clusters visible, mobile)
 *   Λ < 0.35  → Gas phase (chaotic dispersion)
 * 
 * Λ acts as inverse temperature (1/kT): high Λ = cold = ordered
 * S acts as temperature (kT): high S = hot = disordered
 * The separatrix at Λ ≈ 0.35 maps to a critical temperature T_c
 * 
 * @author Dr. José Manuel Cadena Ortiz de Montellano
 * @framework Cadena Strategic Systems — Emergence Theory Layer
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DOMAIN_DEFINITIONS, LAMBDA_WEIGHTS } from '../core/emergence-metrics';
import { CHANNEL_SUPPORT_GROUPS, GAMMA_CHANNEL_ATTENUATION } from '../core/logos';

// ═══════════════════════════════════════════════════════════════
// PHYSICAL CONSTANTS (tuned for 380×380 canvas)
// ═══════════════════════════════════════════════════════════════

const PHYSICS = {
  ALPHA:    0.0030,   // Central coupling (harmonic well strength)
  BETA:     0.0040,   // Radial spring constant (value → radius mapping)
  KAPPA:    0.0018,   // Intra-domain cohesion
  MU:       45,       // Short-range repulsion amplitude
  PSI:      0.0050,   // Mandala geometric attractor strength
  SIGMA_N:  1.8,      // Noise amplitude
  GAMMA:    0.91,     // Velocity damping per frame (1 = no damping)
  D_MIN:    14,       // Repulsion cutoff distance (px)
  R_MAX:    0.42,     // Maximum equilibrium radius (fraction of canvas)
  TRAIL_LEN: 10,      // Trail history length
  DT:       1.0,      // Integration timestep
};

// ═══════════════════════════════════════════════════════════════
// DOMAIN LAYOUT — 7 clusters at 2π k/7 angles
// ═══════════════════════════════════════════════════════════════

const DOMAIN_KEYS = Object.keys(DOMAIN_DEFINITIONS);
const DOMAIN_ANGLES = DOMAIN_KEYS.map((_, k) => (2 * Math.PI * k) / 7 - Math.PI / 2);
const SUB_OFFSETS = [-0.22, -0.07, 0.07, 0.22]; // Angular offsets for 4 dims within domain

// ═══════════════════════════════════════════════════════════════
// SIGMOID — smooth activation for mandala formation
// σ(x) = 1 / (1 + e^{-k·x})
// ═══════════════════════════════════════════════════════════════

function sigmoid(x, k = 12) {
  return 1.0 / (1.0 + Math.exp(-k * x));
}

// ═══════════════════════════════════════════════════════════════
// BOX-MULLER — Gaussian random N(0,1)
// ═══════════════════════════════════════════════════════════════

function gaussRandom() {
  const u1 = Math.random() || 1e-10;
  const u2 = Math.random();
  return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
}

// ═══════════════════════════════════════════════════════════════
// PARTICLE INITIALIZATION
// ═══════════════════════════════════════════════════════════════

function initParticles(cx, cy, R) {
  const particles = [];
  DOMAIN_KEYS.forEach((domKey, domIdx) => {
    const def = DOMAIN_DEFINITIONS[domKey];
    const baseAngle = DOMAIN_ANGLES[domIdx];

    def.dims.forEach((dim, dimIdx) => {
      const subAngle = baseAngle + SUB_OFFSETS[dimIdx];
      const initR = R * (0.3 + Math.random() * 0.4);
      particles.push({
        dim,
        domainKey: domKey,
        domainIdx: domIdx,
        color: def.color,
        domainLabel: def.label,
        x: cx + initR * Math.cos(subAngle),
        y: cy + initR * Math.sin(subAngle),
        vx: 0,
        vy: 0,
        trail: [],
      });
    });
  });
  return particles;
}

// ═══════════════════════════════════════════════════════════════
// MANDALA TARGET POSITIONS
// At A⁺, particles crystallize into sacred geometry:
// 7 domain groups on a ring, 4 dims per group in a sub-ring
// ═══════════════════════════════════════════════════════════════

function computeMandalaTargets(cx, cy, R, lambda) {
  const anchorR = R * (0.18 + 0.22 * (1 - lambda));
  const subR = 10 + 6 * (1 - lambda);
  const targets = [];

  DOMAIN_KEYS.forEach((_, domIdx) => {
    const baseAngle = DOMAIN_ANGLES[domIdx];
    const anchorX = cx + anchorR * Math.cos(baseAngle);
    const anchorY = cy + anchorR * Math.sin(baseAngle);

    for (let j = 0; j < 4; j++) {
      const subAngle = baseAngle + SUB_OFFSETS[j] * 2.5;
      targets.push({
        x: anchorX + subR * Math.cos(subAngle),
        y: anchorY + subR * Math.sin(subAngle),
      });
    }
  });
  return targets;
}

// ═══════════════════════════════════════════════════════════════
// PHYSICS STEP — Euler-Maruyama integration
// ═══════════════════════════════════════════════════════════════

function physicsStep(particles, state, prevState, lambda, entropy, cx, cy, R) {
  const { ALPHA, BETA, KAPPA, MU, PSI, SIGMA_N, GAMMA, D_MIN, R_MAX, DT, TRAIL_LEN } = PHYSICS;
  const mandalaActivation = sigmoid(lambda - 0.60);
  const targets = computeMandalaTargets(cx, cy, R, lambda);

  for (let i = 0; i < particles.length; i++) {
    const p = particles[i];
    const si = state[p.dim] ?? 0.5;
    const sDot = prevState ? (si - (prevState[p.dim] ?? 0.5)) : 0;

    let fx = 0, fy = 0;

    // 1. CENTRAL HARMONIC: F = -α·Λ·(r - r₀)
    const dxC = p.x - cx;
    const dyC = p.y - cy;
    fx -= ALPHA * lambda * lambda * dxC;
    fy -= ALPHA * lambda * lambda * dyC;

    // 2. RADIAL SPRING: equilibrium radius ∝ (1 - s_i)
    const dist = Math.sqrt(dxC * dxC + dyC * dyC) || 0.01;
    const rEq = R * R_MAX * (1 - si * 0.7);
    const radialForce = -BETA * (dist - rEq);
    fx += radialForce * (dxC / dist);
    fy += radialForce * (dyC / dist);

    // 3. INTRA-DOMAIN COHESION
    for (let j = 0; j < particles.length; j++) {
      if (i === j || particles[j].domainIdx !== p.domainIdx) continue;
      const dxD = p.x - particles[j].x;
      const dyD = p.y - particles[j].y;
      const dD = Math.sqrt(dxD * dxD + dyD * dyD) || 0.01;
      fx -= KAPPA * (dxD / dD);
      fy -= KAPPA * (dyD / dD);
    }

    // 4. SHORT-RANGE REPULSION (Lennard-Jones-like, capped)
    for (let j = 0; j < particles.length; j++) {
      if (i === j) continue;
      const dxR = p.x - particles[j].x;
      const dyR = p.y - particles[j].y;
      const dR = Math.sqrt(dxR * dxR + dyR * dyR) || 0.01;
      if (dR < D_MIN) {
        const repF = MU / (dR * dR);
        fx += repF * (dxR / dR);
        fy += repF * (dyR / dR);
      }
    }

    // 5. MANDALA GEOMETRIC ATTRACTOR (activated near A⁺)
    const target = targets[i];
    if (target && mandalaActivation > 0.01) {
      fx -= PSI * mandalaActivation * (p.x - target.x);
      fy -= PSI * mandalaActivation * (p.y - target.y);
    }

    // 6. STOCHASTIC FORCING: F_noise = σ·√S·ξ(t)
    const noiseScale = SIGMA_N * Math.sqrt(Math.max(0.01, entropy)) * Math.sqrt(DT);
    fx += noiseScale * gaussRandom();
    fy += noiseScale * gaussRandom();

    // Euler-Maruyama integration with velocity damping
    p.vx = (p.vx + fx * DT) * GAMMA;
    p.vy = (p.vy + fy * DT) * GAMMA;
    p.x += p.vx * DT;
    p.y += p.vy * DT;

    // Boundary reflection (soft)
    const bR = R * 0.95;
    const bDist = Math.sqrt((p.x - cx) ** 2 + (p.y - cy) ** 2);
    if (bDist > bR) {
      const scale = bR / bDist;
      p.x = cx + (p.x - cx) * scale;
      p.y = cy + (p.y - cy) * scale;
      p.vx *= -0.3;
      p.vy *= -0.3;
    }

    // Store trail
    p.trail.push({ x: p.x, y: p.y });
    if (p.trail.length > TRAIL_LEN) p.trail.shift();

    // Store derivative for glow
    p._sDot = sDot;
    p._si = si;
  }
}

// ═══════════════════════════════════════════════════════════════
// RENDER FRAME
// ═══════════════════════════════════════════════════════════════

function renderFrame(ctx, W, H, particles, lambda, entropy, cx, cy, R, elapsed) {
  // Clear
  ctx.fillStyle = '#0B0A15';
  ctx.fillRect(0, 0, W, H);

  // Background radial gradient (attractor glow)
  const attractorColor = lambda > 0.5 ? 'rgba(245,197,66,' : 'rgba(168,85,247,';
  const glowIntensity = 0.06 + lambda * 0.08;
  const bgGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, R * 0.8);
  bgGrad.addColorStop(0, attractorColor + glowIntensity + ')');
  bgGrad.addColorStop(1, 'transparent');
  ctx.fillStyle = bgGrad;
  ctx.fillRect(0, 0, W, H);

  // Subtle circular boundary
  ctx.strokeStyle = 'rgba(255,255,255,0.04)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(cx, cy, R * 0.92, 0, Math.PI * 2);
  ctx.stroke();

  // Domain anchor labels (faint)
  ctx.font = '8px monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const labelR = R * 0.88;
  DOMAIN_KEYS.forEach((domKey, k) => {
    const angle = DOMAIN_ANGLES[k];
    const lx = cx + labelR * Math.cos(angle);
    const ly = cy + labelR * Math.sin(angle);
    ctx.fillStyle = DOMAIN_DEFINITIONS[domKey].color + '40';
    ctx.fillText(DOMAIN_DEFINITIONS[domKey].label, lx, ly);
  });

  // Intra-domain connection lines (opacity ∝ Λ)
  if (lambda > 0.3) {
    const lineAlpha = Math.min(0.25, (lambda - 0.3) * 0.5);
    for (let domIdx = 0; domIdx < 7; domIdx++) {
      const domParticles = particles.filter(p => p.domainIdx === domIdx);
      ctx.strokeStyle = domParticles[0].color + Math.round(lineAlpha * 255).toString(16).padStart(2, '0');
      ctx.lineWidth = 0.5;
      for (let a = 0; a < domParticles.length; a++) {
        for (let b = a + 1; b < domParticles.length; b++) {
          ctx.beginPath();
          ctx.moveTo(domParticles[a].x, domParticles[a].y);
          ctx.lineTo(domParticles[b].x, domParticles[b].y);
          ctx.stroke();
        }
      }
    }
  }

  // Mandala ring guides (visible near A⁺)
  if (lambda > 0.55) {
    const ringAlpha = sigmoid(lambda - 0.6) * 0.12;
    const anchorR = R * (0.18 + 0.22 * (1 - lambda));
    ctx.strokeStyle = `rgba(245,197,66,${ringAlpha})`;
    ctx.lineWidth = 0.5;
    ctx.setLineDash([2, 4]);
    ctx.beginPath();
    ctx.arc(cx, cy, anchorR, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  // Center attractor pulsing dot
  const pulse = 1 + 0.15 * Math.sin(elapsed * 0.003);
  const centerR = (3 + lambda * 5) * pulse;
  const centerGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, centerR * 3);
  const centerColor = lambda > 0.5 ? '#F5C542' : '#A855F7';
  centerGrad.addColorStop(0, centerColor + 'A0');
  centerGrad.addColorStop(0.5, centerColor + '30');
  centerGrad.addColorStop(1, 'transparent');
  ctx.fillStyle = centerGrad;
  ctx.beginPath();
  ctx.arc(cx, cy, centerR * 3, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = centerColor;
  ctx.beginPath();
  ctx.arc(cx, cy, centerR * 0.5, 0, Math.PI * 2);
  ctx.fill();

  // Particles: trails + circles + glow
  for (const p of particles) {
    // Trail (phosphorescent fade)
    if (p.trail.length > 1) {
      for (let t = 1; t < p.trail.length; t++) {
        const alpha = (t / p.trail.length) * 0.3;
        ctx.strokeStyle = p.color + Math.round(alpha * 255).toString(16).padStart(2, '0');
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(p.trail[t - 1].x, p.trail[t - 1].y);
        ctx.lineTo(p.trail[t].x, p.trail[t].y);
        ctx.stroke();
      }
    }

    // Glow (proportional to |ṡ_i| — changing dimensions glow brighter)
    const glowR = 4 + Math.abs(p._sDot || 0) * 80;
    if (glowR > 5) {
      const glowGrad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, glowR);
      glowGrad.addColorStop(0, p.color + '40');
      glowGrad.addColorStop(1, 'transparent');
      ctx.fillStyle = glowGrad;
      ctx.beginPath();
      ctx.arc(p.x, p.y, glowR, 0, Math.PI * 2);
      ctx.fill();
    }

    // Particle circle (radius ∝ dimension value)
    const radius = 2.5 + (p._si || 0.5) * 3.5;
    ctx.fillStyle = p.color;
    ctx.globalAlpha = 0.55 + (p._si || 0.5) * 0.45;
    ctx.beginPath();
    ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1.0;

    // Particle border
    ctx.strokeStyle = p.color + '80';
    ctx.lineWidth = 0.5;
    ctx.stroke();
  }

  // Phase indicator text
  ctx.font = 'bold 9px monospace';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  const phaseText = lambda > 0.65 ? 'AUTO-ORGANIZÁNDOSE'
    : lambda > 0.35 ? 'FASE FLUIDA'
    : 'DISPERSIÓN';
  const phaseColor = lambda > 0.65 ? '#10B981' : lambda > 0.35 ? '#F5C542' : '#EF4444';
  ctx.fillStyle = phaseColor + 'B0';
  ctx.fillText(phaseText, 10, 10);

  // Λ and S readout
  ctx.font = '8px monospace';
  ctx.fillStyle = '#8888AAA0';
  ctx.textAlign = 'right';
  ctx.fillText(`Λ=${lambda.toFixed(3)}  S=${entropy.toFixed(3)}`, W - 10, 10);
  ctx.fillText(`28 dimensiones · 7 dominios`, W - 10, 22);
}

// ═══════════════════════════════════════════════════════════════
// SCENARIO METRICS — recompute Λ and S from a modified state
// Simplified inline computation matching core engine logic
// ═══════════════════════════════════════════════════════════════

function computeScenarioLambda(scenarioState) {
  let sum = 0;
  for (const [dim, w] of Object.entries(LAMBDA_WEIGHTS)) {
    sum += w * (scenarioState[dim] ?? 0.5);
  }
  const lambdaSignal = Math.max(0, Math.min(1, sum));

  // Φ_channel: channel quality modulation (Hierarchical Lambda)
  let weightedAvg = 0;
  for (const group of Object.values(CHANNEL_SUPPORT_GROUPS)) {
    const groupAvg = group.items.reduce((s, it) => s + (scenarioState[it.key] ?? 0.5), 0) / group.items.length;
    weightedAvg += group.weight * groupAvg;
  }
  const phi = 1 - GAMMA_CHANNEL_ATTENUATION * (1 - weightedAvg);

  return Math.max(0, Math.min(1, lambdaSignal * phi));
}

function computeScenarioEntropy(scenarioState) {
  const entropyDims = ['sleep', 'nutrition', 'exercise', 'peace', 'clarity', 'focus',
    'faith', 'meditation', 'family', 'community', 'meaning', 'service',
    'nourishment', 'gut_resonance'];
  let deficit = 0;
  for (const d of entropyDims) {
    const v = scenarioState[d] ?? 0.5;
    if (v < 0.5) deficit += (0.5 - v);
  }
  return Math.max(0, Math.min(1, deficit / (entropyDims.length * 0.25)));
}

// ═══════════════════════════════════════════════════════════════
// BUILD SCENARIO STATE — apply domain-level slider overrides
// ═══════════════════════════════════════════════════════════════

function buildScenarioState(baseState, domainOverrides) {
  const s = { ...baseState };
  for (const [domKey, targetVal] of Object.entries(domainOverrides)) {
    const def = DOMAIN_DEFINITIONS[domKey];
    if (!def) continue;
    const currentAvg = def.dims.reduce((sum, d) => sum + (baseState[d] ?? 0.5), 0) / def.dims.length;
    const delta = targetVal - currentAvg;
    for (const d of def.dims) {
      s[d] = Math.max(0, Math.min(1, (baseState[d] ?? 0.5) + delta));
    }
  }
  return s;
}

// ═══════════════════════════════════════════════════════════════
// STYLES
// ═══════════════════════════════════════════════════════════════

const UI = {
  bg: '#0B0A15', card: '#111020', border: '#2D2D44',
  text: '#E0E0F0', textDim: '#8888AA', accent: '#C084FC',
};

// ═══════════════════════════════════════════════════════════════
// REACT COMPONENT
// ═══════════════════════════════════════════════════════════════

export default function EmergenceSwarmAnimation({
  state,
  prevState = null,
  lambda = 0.5,
  entropy = 0.5,
  width = 380,
  height = 380,
}) {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const particlesRef = useRef(null);
  const animRef = useRef(null);

  // What-If mode
  const [whatIf, setWhatIf] = useState(false);
  const [domainSliders, setDomainSliders] = useState(null); // null = not initialized

  // Compute current domain averages (for initial slider values)
  const domainAvgs = useMemo(() => {
    const avgs = {};
    DOMAIN_KEYS.forEach(domKey => {
      const dims = DOMAIN_DEFINITIONS[domKey].dims;
      avgs[domKey] = dims.reduce((s, d) => s + (state[d] ?? 0.5), 0) / dims.length;
    });
    return avgs;
  }, [state]);

  // Initialize sliders when entering What-If mode
  useEffect(() => {
    if (whatIf && !domainSliders) {
      setDomainSliders({ ...domainAvgs });
    }
  }, [whatIf, domainAvgs, domainSliders]);

  // Reset sliders when leaving What-If
  useEffect(() => {
    if (!whatIf) setDomainSliders(null);
  }, [whatIf]);

  // Effective state/metrics for the animation
  const effectiveState = useMemo(() => {
    if (!whatIf || !domainSliders) return state;
    return buildScenarioState(state, domainSliders);
  }, [whatIf, domainSliders, state]);

  const effectiveLambda = useMemo(() => {
    if (!whatIf || !domainSliders) return lambda;
    return computeScenarioLambda(effectiveState);
  }, [whatIf, domainSliders, effectiveState, lambda]);

  const effectiveEntropy = useMemo(() => {
    if (!whatIf || !domainSliders) return entropy;
    return computeScenarioEntropy(effectiveState);
  }, [whatIf, domainSliders, effectiveState, entropy]);

  // Stable references for animation loop
  const stateRef = useRef(effectiveState);
  const prevStateRef = useRef(prevState);
  const lambdaRef = useRef(effectiveLambda);
  const entropyRef = useRef(effectiveEntropy);

  useEffect(() => { stateRef.current = effectiveState; }, [effectiveState]);
  useEffect(() => { prevStateRef.current = whatIf ? state : prevState; }, [prevState, state, whatIf]);
  useEffect(() => { lambdaRef.current = effectiveLambda; }, [effectiveLambda]);
  useEffect(() => { entropyRef.current = effectiveEntropy; }, [effectiveEntropy]);

  // Phase description
  const phaseLabel = useMemo(() => {
    const l = effectiveLambda;
    if (l > 0.65) return 'Auto-organizándose';
    if (l > 0.35) return 'Fase fluida';
    return 'Dispersión';
  }, [effectiveLambda]);

  // Canvas animation loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = window.devicePixelRatio || 2;
    const W = width;
    const H = height;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    const cx = W / 2;
    const cy = H / 2;
    const R = Math.min(W, H) / 2 - 20;

    if (!particlesRef.current) {
      particlesRef.current = initParticles(cx, cy, R);
    }

    function animate(timestamp) {
      physicsStep(particlesRef.current, stateRef.current, prevStateRef.current,
        lambdaRef.current, entropyRef.current, cx, cy, R);
      renderFrame(ctx, W, H, particlesRef.current, lambdaRef.current,
        entropyRef.current, cx, cy, R, timestamp);
      animRef.current = requestAnimationFrame(animate);
    }

    animRef.current = requestAnimationFrame(animate);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [width, height]);

  // Slider change handler
  const handleSlider = (domKey, val) => {
    setDomainSliders(prev => ({ ...prev, [domKey]: parseFloat(val) }));
  };

  // Delta indicators
  const deltaLambda = effectiveLambda - lambda;
  const deltaEntropy = effectiveEntropy - entropy;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* MODE TOGGLE */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            onClick={() => setWhatIf(false)}
            style={{
              padding: '4px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
              fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.05em',
              background: !whatIf ? UI.accent : 'rgba(255,255,255,0.05)',
              color: !whatIf ? '#fff' : UI.textDim,
              transition: 'all 0.2s',
            }}
          >
            ESTADO ACTUAL
          </button>
          <button
            onClick={() => setWhatIf(true)}
            style={{
              padding: '4px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
              fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.05em',
              background: whatIf ? '#F5C542' : 'rgba(255,255,255,0.05)',
              color: whatIf ? '#0B0A15' : UI.textDim,
              transition: 'all 0.2s',
            }}
          >
            WHAT-IF
          </button>
        </div>
        {whatIf && (
          <button
            onClick={() => setDomainSliders({ ...domainAvgs })}
            style={{
              padding: '3px 10px', borderRadius: 5, border: `1px solid ${UI.border}`,
              cursor: 'pointer', fontFamily: 'monospace', fontSize: 8,
              background: 'transparent', color: UI.textDim,
            }}
          >
            RESET
          </button>
        )}
      </div>

      {/* CANVAS */}
      <div style={{ position: 'relative' }}>
        <canvas
          ref={canvasRef}
          style={{
            width, height, borderRadius: 16,
            border: whatIf ? '1px solid rgba(245,197,66,0.25)' : '1px solid rgba(255,255,255,0.06)',
            transition: 'border-color 0.3s',
          }}
          aria-label={`Emergence Swarm: ${phaseLabel}`}
        />
        <div style={{
          position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(11,10,21,0.85)', borderRadius: 20, padding: '4px 14px',
          fontSize: 10, fontFamily: 'monospace', whiteSpace: 'nowrap',
          color: whatIf ? '#F5C542' : '#8888AA',
          border: whatIf ? '1px solid rgba(245,197,66,0.2)' : '1px solid rgba(255,255,255,0.06)',
        }}>
          {whatIf ? t('misc.whatIfScenario') : 'Langevin dynamics'} · {phaseLabel}
        </div>
        {whatIf && (
          <div style={{
            position: 'absolute', top: 8, left: 8,
            background: 'rgba(245,197,66,0.12)', borderRadius: 8, padding: '3px 8px',
            fontSize: 8, fontFamily: 'monospace', color: '#F5C542',
            border: '1px solid rgba(245,197,66,0.2)',
          }}>
            {t('misc.simulation')}
          </div>
        )}
      </div>

      {/* WHAT-IF SLIDERS */}
      {whatIf && domainSliders && (
        <div style={{
          background: UI.card, borderRadius: 12, padding: 14,
          border: '1px solid rgba(245,197,66,0.15)',
        }}>
          <div style={{
            fontSize: 9, fontFamily: 'monospace', color: '#F5C542',
            letterSpacing: '0.1em', marginBottom: 10, textAlign: 'center',
          }}>
            MODELAR ESCENARIO — AJUSTA DOMINIOS
          </div>

          {DOMAIN_KEYS.map(domKey => {
            const def = DOMAIN_DEFINITIONS[domKey];
            const current = domainAvgs[domKey];
            const scenario = domainSliders[domKey] ?? current;
            const delta = scenario - current;
            const improved = delta > 0.02;
            const declined = delta < -0.02;

            return (
              <div key={domKey} style={{ marginBottom: 8 }}>
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', marginBottom: 2,
                }}>
                  <span style={{ fontSize: 10, fontFamily: 'monospace', color: def.color }}>
                    {def.label}
                  </span>
                  <span style={{
                    fontSize: 9, fontFamily: 'monospace', fontWeight: 600,
                    color: improved ? '#10B981' : declined ? '#EF4444' : UI.textDim,
                  }}>
                    {(scenario * 100).toFixed(0)}%
                    {Math.abs(delta) > 0.02 && (
                      <span style={{ marginLeft: 4, fontSize: 8 }}>
                        ({delta > 0 ? '+' : ''}{(delta * 100).toFixed(0)}%)
                      </span>
                    )}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{
                    width: 4, height: 4, borderRadius: 2,
                    background: def.color + '60',
                  }} />
                  <input
                    type="range" min="0" max="1" step="0.01"
                    value={scenario}
                    onChange={e => handleSlider(domKey, e.target.value)}
                    style={{
                      flex: 1, height: 4, appearance: 'none', WebkitAppearance: 'none',
                      background: `linear-gradient(90deg, ${def.color}60 ${scenario * 100}%, ${UI.border} ${scenario * 100}%)`,
                      borderRadius: 2, outline: 'none', cursor: 'pointer',
                      accentColor: def.color,
                    }}
                  />
                  <div style={{
                    width: 4, height: 4, borderRadius: 2,
                    background: def.color,
                  }} />
                </div>
              </div>
            );
          })}

          {/* PROJECTED METRICS */}
          <div style={{
            marginTop: 12, padding: 10, borderRadius: 8,
            background: 'rgba(255,255,255,0.03)',
            border: `1px solid ${UI.border}`,
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8,
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 8, fontFamily: 'monospace', color: UI.textDim, letterSpacing: '0.1em' }}>Λ PROYECTADO</div>
              <div style={{
                fontSize: 18, fontFamily: 'monospace', fontWeight: 700,
                color: effectiveLambda > 0.65 ? '#10B981' : effectiveLambda > 0.35 ? '#F5C542' : '#EF4444',
              }}>
                {effectiveLambda.toFixed(3)}
              </div>
              {Math.abs(deltaLambda) > 0.005 && (
                <div style={{
                  fontSize: 9, fontFamily: 'monospace', fontWeight: 600,
                  color: deltaLambda > 0 ? '#10B981' : '#EF4444',
                }}>
                  {deltaLambda > 0 ? '▲' : '▼'} {(deltaLambda > 0 ? '+' : '')}{deltaLambda.toFixed(3)}
                </div>
              )}
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 8, fontFamily: 'monospace', color: UI.textDim, letterSpacing: '0.1em' }}>S PROYECTADA</div>
              <div style={{
                fontSize: 18, fontFamily: 'monospace', fontWeight: 700,
                color: effectiveEntropy < 0.35 ? '#10B981' : effectiveEntropy < 0.60 ? '#F5C542' : '#EF4444',
              }}>
                {effectiveEntropy.toFixed(3)}
              </div>
              {Math.abs(deltaEntropy) > 0.005 && (
                <div style={{
                  fontSize: 9, fontFamily: 'monospace', fontWeight: 600,
                  color: deltaEntropy < 0 ? '#10B981' : '#EF4444',
                }}>
                  {deltaEntropy < 0 ? '▼' : '▲'} {deltaEntropy < 0 ? '' : '+'}{deltaEntropy.toFixed(3)}
                </div>
              )}
            </div>
          </div>

          {/* PHASE PROJECTION */}
          {effectiveLambda !== lambda && (
            <div style={{
              marginTop: 8, textAlign: 'center', fontSize: 10, fontFamily: 'monospace',
              color: effectiveLambda > 0.65 ? '#10B981' : effectiveLambda > 0.35 ? '#F5C542' : '#EF4444',
              padding: '6px 0',
            }}>
              {effectiveLambda > 0.65
                ? '✦ Este escenario alcanza la fase de auto-organización (A⁺)'
                : effectiveLambda > 0.35
                  ? '◈ Fase fluida — clusters parciales, aún no cristaliza'
                  : '⚠ Dispersión — necesitas subir más dominios'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
